import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { EmptyState, ListTable, Panel, StatusChip, type ListColumn } from '../../../ui-kit';
import { fetchCqConfiguration, type CqConfiguration } from '../../../services/carrierQuotingService';
import { useLive } from '../rocco/live';
import { BrandLogo } from './logos';

type CarrierRow = CqConfiguration['carriers'][number];

/**
 * How Carrier Quoting is set up, exactly as it runs: which carriers it asks
 * and how each is reached, the packages of cover it prices, what it flags,
 * and the rules it keeps. Nothing here is a control.
 */
export default function ConfigurationPage() {
  const { data, error, loading } = useLive(fetchCqConfiguration, 60_000);
  if (error && !data) return <EmptyState message={error} />;

  const columns: ListColumn<CarrierRow>[] = [
    {
      key: 'name',
      sortValue: (r) => r.name,
      header: 'Carrier',
      grow: 1.3,
      minWidth: 210,
      render: (r) => (
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
          <BrandLogo id={r.key} name={r.name} size={28} />
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{r.name}</Typography>
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              {r.company} · NAIC {r.naic}
            </Typography>
          </Stack>
        </Stack>
      ),
    },
    {
      key: 'program',
      sortValue: (r) => r.program,
      header: 'Product',
      grow: 1,
      minWidth: 170,
      render: (r) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{r.program}</Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{r.terms}</Typography>
        </Stack>
      ),
    },
    {
      key: 'route',
      sortValue: (r) => r.route_label,
      header: 'Asked through',
      grow: 1,
      minWidth: 200,
      render: (r) => (
        <Stack alignItems="flex-start" spacing={0.25}>
          <StatusChip tone={r.route === 'rater' ? 'good' : 'warning'}
            label={r.route === 'rater' ? 'Rater' : 'A person submits'} />
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{r.route_label}</Typography>
        </Stack>
      ),
    },
    {
      key: 'code',
      sortValue: (r) => r.producer_code,
      header: 'Producer code',
      width: 140,
      render: (r) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, fontVariantNumeric: 'tabular-nums' })}>
          {r.producer_code}
        </Typography>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      <Panel
        title="The carriers it asks"
        subtitle={data ? `${data.carriers.length} carriers on every household` : 'On every household'}
      >
        <ListTable
          columns={columns}
          rows={data?.carriers ?? []}
          getRowKey={(r) => r.key}
          loading={loading}
          emptyMessage="No carriers are set up."
        />
      </Panel>

      <Panel title="The cover it prices" subtitle="Every carrier is asked for each of these, so the answers line up"
        loading={loading} loadingHeight={140}>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
          {(data?.packages ?? []).map((one) => <Line key={one.name} title={one.name} detail={one.detail} />)}
        </Box>
      </Panel>

      <Panel title="How a quote runs" subtitle="From the paperwork landing to the proposal"
        loading={loading} loadingHeight={300}>
        <Stack>
          {(data?.rules ?? []).map((rule) => (
            <Box
              key={rule.when}
              sx={(t) => ({
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(200px, 1fr) minmax(200px, 1fr) 2fr' },
                columnGap: 2,
                rowGap: 0.25,
                py: 1.25,
                borderBottom: `1px solid ${t.tokens.border.subtle}`,
                '&:last-of-type': { borderBottom: 'none' },
              })}
            >
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{rule.when}</Typography>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>{rule.then}</Typography>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>{rule.detail}</Typography>
            </Box>
          ))}
        </Stack>
      </Panel>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr 1fr' }, alignItems: 'start' }}>
        <Panel title="What it reads" subtitle="The same fixed form from every document"
          loading={loading} loadingHeight={220}>
          <Stack spacing={1.25}>
            {(data?.reads ?? []).map((one) => <Line key={one.kind} title={one.label} detail={one.detail} />)}
          </Stack>
        </Panel>

        <Panel title="What it flags" subtitle="Each one found by a rule" loading={loading} loadingHeight={220}>
          <Stack spacing={1}>
            {(data?.flags ?? []).map((flag) => (
              <Stack key={flag.label} direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{ flexShrink: 0 }}>
                  <StatusChip tone={flag.severity === 'Important' ? 'bad' : 'neutral'} label={flag.severity} />
                </Box>
                <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{flag.label}</Typography>
              </Stack>
            ))}
          </Stack>
        </Panel>

        <Panel title="Guardrails" subtitle="Held on every document and every quote"
          loading={loading} loadingHeight={220}>
          <Stack spacing={1.25}>
            {(data?.guardrails ?? []).map((rail) => <Line key={rail.title} title={rail.title} detail={rail.detail} />)}
          </Stack>
        </Panel>
      </Box>
    </Stack>
  );
}

function Line({ title, detail }: { title: string; detail: string }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>{title}</Typography>
      <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{detail}</Typography>
    </Stack>
  );
}
